import { inject, Injectable, signal } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures } from 'app/core/enums/common.enum';
import { LocationService } from 'app/modules/sho_management_pro/locations/services/location.service';
import { PlaydateService } from 'app/modules/sho_management_pro/play-dates/services/playdate.service';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { CommissionService } from '../../services/commission.service';
import * as CommissionActions from './commissions.actions';

@Injectable()
export class CommissionsEffects {
    private _actions$ = inject(Actions);
    private _commissionService = inject(CommissionService);
    private _alertService = inject(AlertService);
    private _adapter = inject(AdapterService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _playdatesService = inject(PlaydateService);
    private _locationService = inject(LocationService);

    private currentDeparture = signal<Departures>(null);

    messages = {
        getAllError: 'Error getting all commissions',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Commissions
    getAllCommissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.loadCommissions),
            switchMap((action) => {
                return this._commissionService
                    .getAll(action.playdateId, action.departure)
                    .pipe(
                        map((res) => {
                            // Set current departure on load the commissions
                            // This assumes what view is currently displaying
                            this.currentDeparture.set(action.departure);

                            const commissions =
                                this._adapter.adaptAllCommissions(res.data);

                            return CommissionActions.loadCommissionsSuccess({
                                commissions: commissions,
                            });
                        }),
                        catchError((error) =>
                            of(
                                CommissionActions.loadCommissionsFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Get One Commission
    getCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.loadCommission),
            switchMap((action) => {
                return this._commissionService.getOne(action.id).pipe(
                    map((res) => {
                        const data = res.data;
                        const commission = this._adapter.adaptSingleCommission(
                            data.commissions[0]
                        );
                        return CommissionActions.loadCommissionSuccess({
                            commission: commission,
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommissionActions.loadCommissionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Commission
    createCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.createCommission),
            switchMap((action) => {
                return this._commissionService.create(action.data).pipe(
                    // tap((res) => console.log(res)), // Debug
                    map((res) => {
                        const data = res.data;
                        const commission = this._adapter.adaptSingleCommission(
                            data.commissions[0]
                        );
                        return CommissionActions.createCommissionSuccess({
                            commission,
                            success: 'Commission created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommissionActions.createCommissionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Commission
    updateCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.updateCommission),
            switchMap((action) => {
                return this._commissionService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            const data = res.data;
                            const commission =
                                this._adapter.adaptSingleCommission(
                                    data.commissions[0]
                                );
                            return CommissionActions.updateCommissionSuccess({
                                commission,
                                success: 'Commission updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                CommissionActions.updateCommissionFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Update Sales Rep Rate
    updateSalesRepRate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.updateSalesRepRate),
            switchMap((action) => {
                return this._commissionService
                    .updateRate(action.data, action.id)
                    .pipe(
                        map((res) => {
                            const data = res.data;
                            const commission =
                                this._adapter.adaptSingleCommission(
                                    data.commissions[0]
                                );
                            return CommissionActions.updateSalesRepRateSuccess({
                                commission,
                                success: 'Sales Rep rate updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                CommissionActions.updateSalesRepRateFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Reset Commission
    resetCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.resetCommission),
            switchMap((action) => {
                return this._commissionService
                    .reset(
                        action.playdateId,
                        action.departure,
                        action.locationId
                    )
                    .pipe(
                        map((res) => {
                            const data = res.data;
                            const commission =
                                this._adapter.adaptSingleCommission(
                                    data.commissions[0]
                                );
                            return CommissionActions.resetCommissionSuccess({
                                commission: commission,
                                success: 'Commissions reset successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                CommissionActions.resetCommissionFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Commission
    deleteCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.deleteCommission),
            switchMap((action) => {
                return this._commissionService.delete(action.id).pipe(
                    map((res) => {
                        return CommissionActions.deleteCommissionSuccess({
                            id: action.id,
                            success: 'Commission deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommissionActions.deleteCommissionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Parking | Other Commission
    deletePkOtCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.deletePkOtCommission),
            switchMap((action) => {
                return this._commissionService.deletePkOt(action.params).pipe(
                    map((res) => {
                        return CommissionActions.deletePkOtCommissionSuccess({
                            id: action.id,
                            success: 'Commission deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommissionActions.deletePkOtCommissionFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sales Rep from Commission
    deleteSalesRepFromCommission$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommissionActions.deleteCommissionSalesRep),
            switchMap((action) => {
                return this._commissionService.deleteSalesRep(action.id).pipe(
                    map((res) => {
                        const data = res.data;
                        const commission = this._adapter.adaptSingleCommission(
                            data.commissions[0]
                        );
                        return CommissionActions.deleteCommissionSalesRepSuccess(
                            {
                                commission,
                                success: 'Sales Rep deleted successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            CommissionActions.deleteCommissionSalesRepFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onCommissionActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    CommissionActions.createCommissionSuccess,
                    CommissionActions.updateCommissionSuccess,
                    CommissionActions.updateSalesRepRateSuccess,
                    CommissionActions.deleteCommissionSuccess,
                    CommissionActions.deletePkOtCommissionSuccess,
                    CommissionActions.deleteCommissionSalesRepSuccess,
                    CommissionActions.resetCommissionSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;

                    // Get playdate id & departure
                    const playdateId =
                        this._playdatesService.getLatestPlaydateId();
                    const departure = this.currentDeparture();

                    this._locationService.checkCommissionsLocations(
                        playdateId,
                        departure
                    );
                })
            ),
        { dispatch: false }
    );

    onCommissionActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    CommissionActions.loadCommissionsFailure,
                    CommissionActions.loadCommissionFailure,
                    CommissionActions.createCommissionFailure,
                    CommissionActions.updateCommissionFailure,
                    CommissionActions.updateSalesRepRateFailure,
                    CommissionActions.deleteCommissionFailure,
                    CommissionActions.deletePkOtCommissionFailure,
                    CommissionActions.deleteCommissionSalesRepFailure,
                    CommissionActions.resetCommissionFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
